import { useMutation } from '@apollo/client/react';
import '@smastrom/react-rating/style.css';
import {
  type CompleteBookingFragment,
  CreateReviewDocument,
  type Maybe,
  type Review,
  type ReviewInput,
} from '@drycut/graphql';
import { ActionButtons, Button, FormHelpText, Heading, Para, Stack } from '@drycut/ui-components';
import { Rating } from '@smastrom/react-rating';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';

import { ReviewConfirmation } from './ReviewConfirmation';

type Props = {
  booking?: Maybe<CompleteBookingFragment>;
};

export function BookingReview({ booking }: Props): React.ReactElement {
  const [submitReview] = useMutation(CreateReviewDocument);
  const { value: reviewSaved, setTrue: setSaved } = useBoolean(false);

  const formMethods = useForm<Review>({
    defaultValues: {
      score: 0,
      description: '',
    },
  });

  const onSubmit = async (data: ReviewInput) => {
    await submitReview({
      variables: {
        input: {
          bookingId: booking?.id || '',
          data,
        },
      },
    });

    setSaved();
  };

  if (reviewSaved) {
    return <ReviewConfirmation customer={booking?.customer} />;
  }

  return (
    <Stack as="form" gap={6} onSubmit={formMethods.handleSubmit(onSubmit)} className="h-full">
      <Stack gap={7} className="flex-grow items-center font-aribau text-center text-pretty">
        <Heading variant="h2">Deine Bewertung</Heading>

        <Para>Vielen Dank für Deinen Besuch bei DRYCUT und Deine ehrliche Bewertung.</Para>

        <Para>Wie hat Dir Dein Besuch bei DRYCUT gefallen?</Para>

        <FormProvider {...formMethods}>
          <Stack gap={4} className="items-center">
            <Controller
              rules={{
                required: 'Bitte gib Dein Feedback ein.',
                min: { value: 1, message: 'Bitte gib Dein Feedback ein.' },
              }}
              name="score"
              render={({ field, fieldState: { error, invalid } }) => (
                <>
                  <Rating style={{ maxWidth: 280, width: '100%' }} {...field} />
                  <FormHelpText error={invalid}>{error?.message}</FormHelpText>
                </>
              )}
            />

            <label htmlFor="feedback" className="font-aribaubold">
              Hast Du Anregungen für uns?
            </label>

            <Controller
              name="description"
              render={({ field }) => (
                <textarea
                  id="feedback"
                  className="w-72 h-32 font-aribau text-pretty resize-none bg-transparent border border-zinc-300 p-2 focus:ring-primary-300 focus:ring-2 focus:border-zinc-300"
                  placeholder="Dein Feedback"
                  {...field}
                />
              )}
            />
          </Stack>
        </FormProvider>
      </Stack>

      <ActionButtons>
        <Button type="submit">Senden</Button>
      </ActionButtons>
    </Stack>
  );
}
